import { Observable, of, EMPTY } from 'rxjs';
import { mergeMap, throttleTime, map, catchError, switchMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import Global from '../../../store/global-selectors';
import get from 'lodash.get';
import moment from 'moment';

import { ModalService } from 'ui-components/services/modal-service';
import { ShipmentDetailsModalContainer } from '../../../components/modals/shipment-details-modal/shipment-details-modal-container';
import { OrderDetailsHelperService } from '../../../services/order-details-helper-service';
import { GuidService } from 'ui-components/services/guid-service';
import { STANDARD, LOCAL_DELIVERY } from '../../../enums/csw-types';
import * as CSWActions from '../../../store/csw/csw.actions';
import * as PrintActions from '../../../store/printing/print.actions';
import * as LabelsActions from '../../../store/labels/labels.actions';
import * as MarkAsShippedActions from '../../../store/mark-as-shipped/mark-as-shipped.actions';
import * as Actions from './shipments.details.actions';
import * as ShipmentDetailsApi from './shipments.details.api';
import * as AddressApi from '../../../address/address.api';
import { updateFulfillmentPlanConfig } from '../../../store/csw/csw.api';
import { purchaseBulkLabels } from '../../../store/orders/orders.actions';
import { startBatchesShipmentPurchase } from '../../../store/batches/purchase/batches.purchase.actions';
import { VER, ERROR, WARNING } from '../../../enums/address-verification-status';
import { LabelBatchStatusModal } from '../../../components/modals/label-batch-status/';
import { LabelBatchProcessingModalContainer } from '../../../components/modals/label-batch-status/processing/modal-container';
import { LabelBatchStatusStep } from '../../../enums/label-batch-status-step';
import { bulkLoadFulfillmentPlans$ } from '../../bulkload/bulkload.observables';
import { getIsServiceLocalDelivery } from '../../../hooks/use-is-service-local-delivery';
import { DocumentType } from '../../../enums/document-type';

const defaultShipmentDetailsProps = {
  height: '100%',
  width: '100%',
  maxWidth: '100%',
  maxHeight: '100%',
};

export const loadShipmentDetails = (action$) => {
  return action$.pipe(
    ofType(Actions.LOAD_SHIPMENT_DETAILS),
    mergeMap(({ shipmentId }) => {
      return of(Actions.requestShipmentDetails(shipmentId));
    }),
  );
};

export const goToShipmentDetailsForFulfillmentId = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_DETAILS_GO_TO_ROUTE_FOR_FULFILLMENT),
    mergeMap(({ fulfillmentId }) => {
      const state = getState();
      const { fulfillmentPlanId } = Global.selectEntitiesFulfillmentById(state, { fulfillmentId });

      return of(Actions.goToShipmentDetails(fulfillmentPlanId));
    }),
  );
};
// SPD-14016
export const goToShipmentDetails = (action$, _, { push, history }) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_DETAILS_GO_TO_ROUTE),
    mergeMap(({ fulfillmentPlanId }) => {
      const search = history.location.search;
      const historyPathName = history.location.pathname;
      const pathBeginning = historyPathName.includes('/shipment/')
        ? historyPathName.substr(0, historyPathName.indexOf('/shipment/'))
        : historyPathName;
      const shipmentPathParts = ['shipment', fulfillmentPlanId];
      const pathname = [pathBeginning, ...shipmentPathParts].join('/');

      push({
        pathname,
        search,
      });
      return EMPTY;
    }),
  );
};

export const openShipmentDetailsFromLabelBatchStatusProcessingStep = (action$) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_DETAILS_MODAL_FROM_LABEL_BATCH_STATUS_PROCESSING),
    mergeMap(({ fulfillmentPlanId, batchId }) => {
      const shipmentDetailModalId = GuidService.newGuid();
      const preventRouting = true;
      const stringBatchId = batchId.toString();
      const modalId = ModalService.launchModal({
        component: ShipmentDetailsModalContainer,
        onClose: () => {
          ModalService.launchModal({
            component: LabelBatchStatusModal,
            props: {
              step: LabelBatchStatusStep.Processing.ordinal,
              StepComponent: LabelBatchProcessingModalContainer,
              batchId: stringBatchId,
              batchIds: [stringBatchId],
            },
          });
        },
        props: {
          shipmentId: fulfillmentPlanId,
          shipmentDetailModalId,
        },
        ...defaultShipmentDetailsProps,
      });
      return of(Actions.openShipmentDetailModal(fulfillmentPlanId, modalId, shipmentDetailModalId, preventRouting));
    }),
  );
};

// TODO: will be replaced by <AsModal /> implementation
export const openShipmentDetailsFromLabelBatchStatusModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_DETAILS_MODAL_OPEN_FROM_LABEL_BATCH_STATUS),
    mergeMap(({ fulfillmentPlanId, batchId }) => {
      const state = getState();
      const shipmentDetailModalId = GuidService.newGuid();
      const preventRouting = true;
      const activeModalStep = Global.selectBulkActiveModalStep(state);
      const batchEntity = Global.selectEntitiesBatchByFulfillmentPlanSetId(state, { fulfillmentPlanSetId: batchId });
      // TODO: we should start using shouldPrint everywhere
      const printForBulk = activeModalStep ? activeModalStep.andPrint : false;
      const printForBatch = batchEntity ? batchEntity.shouldPrint : false;
      const shouldPrint = printForBulk || printForBatch;
      const onCloseAction = batchEntity ? startBatchesShipmentPurchase(batchId, shouldPrint) : purchaseBulkLabels(shouldPrint);

      return new Observable((observer) => {
        const modalId = ModalService.launchModal({
          component: ShipmentDetailsModalContainer,
          onClose: () => {
            observer.next(onCloseAction);
            observer.complete();
          },
          props: {
            shipmentId: fulfillmentPlanId,
            shipmentDetailModalId,
          },
          ...defaultShipmentDetailsProps,
        });
        observer.next(Actions.openShipmentDetailModal(fulfillmentPlanId, modalId, shipmentDetailModalId, preventRouting));
      }).pipe(mergeMap((next) => of(next)));
    }),
  );
};

export const getShipmentDetailsEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.GET_SHIPMENT_DETAILS_REQUEST),
    mergeMap((action) => {
      const { shipmentDetailModalId } = action;
      const state = getState();
      const fulfillmentPlanId = Global.selectShipmentDetailsFulfillmentPlanIdForShipmentDetailModalId(state, { shipmentDetailModalId });
      if (!fulfillmentPlanId) {
        return EMPTY;
      }

      return bulkLoadFulfillmentPlans$([fulfillmentPlanId]).pipe(
        map(() => {
          return Actions.getShipmentDetailsSuccess(fulfillmentPlanId, shipmentDetailModalId);
        }),
        catchError((error) => {
          return of(Actions.getShipmentDetailsFailure(fulfillmentPlanId, shipmentDetailModalId, error));
        }),
      );
    }),
  );
};

export const refreshShipmentDetailsEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(
      PrintActions.MARK_LABEL_PRINTED_SUCCESS,
      PrintActions.MARK_PACKING_SLIP_PRINTED_SUCCESS,
      CSWActions.CSW_CREATE_LABEL_SUCCESS,
      CSWActions.CSW_SEND_EXTERNAL_FULFILLMENT_SUCCESS,
      LabelsActions.VOID_LABELS_SUCCESS,
      MarkAsShippedActions.SHIPMENT_DETAILS_UNDO_MARK_SHIPPED,
    ),
    throttleTime(2000),
    switchMap(() => {
      // TODO: better way to get shipment details modal ID if printing being done from modal. Might need to make actions specific to shipment details modal.
      const shipmentDetailsModalIds = Global.selectShipmentDetailsShipmentDetailsModalIds(getState());
      if (shipmentDetailsModalIds.length) {
        const id = shipmentDetailsModalIds[shipmentDetailsModalIds.length - 1];
        return of(Actions.requestShipmentDetails(id));
      }
      return EMPTY;
    }),
  );
};

// SPD-14016 this one does actually request shipment details... so there is some use to it, I could be convinced to keep it
export const openShipmentDetailsModal = (action$, _, { push, history }) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_DETAILS_MODAL_OPEN),
    mergeMap((action) => {
      const { shipmentId, shipmentDetailModalId, preventRouting } = action;
      const search = history.location.search;
      const historyPathName = history.location.pathname;
      const pathname = [historyPathName, 'shipment', shipmentId].join('/');
      const shouldPreventRouting = preventRouting || historyPathName.includes('/shipment/') || historyPathName.includes('/alerts/');
      if (!shouldPreventRouting) {
        push({
          pathname,
          search,
        });
      }

      return of(Actions.requestShipmentDetails(shipmentDetailModalId));
    }),
  );
};

// TODO: Examine need for this epic going forward SPD-14016 this one does nothing or navigates. can probably go
export const closeShipmentDetailsModal = (action$, _, { push, history }) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_DETAILS_MODAL_CLOSED),
    mergeMap(() => {
      const search = history.location.search;
      const historyPathName = history.location.pathname;
      const shipmentDetailsEndRouteRegex = /\/shipment\/\d+\/?$/; // path ends with `/shipment/#####` or `/shipment/#####/`
      if (!historyPathName.match(shipmentDetailsEndRouteRegex)) {
        return EMPTY;
      }

      const pathNameSplit = historyPathName.split('/');
      const pathname = pathNameSplit.slice(0, pathNameSplit.length - 2).join('/'); // Remove the last two parts of the path: `/shipping/:fulfillmentplanId`

      push({ pathname, search });
      return EMPTY;
    }),
  );
};

export const persistShipmentConfigurationEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.GET_SHIPMENT_DETAILS_SUCCESS),
    mergeMap((action) => {
      const state = getState();
      const { shipment } = action;
      const { fulfillmentPlan } = shipment ?? {};
      if (!fulfillmentPlan) {
        return EMPTY;
      }
      const { fulfillmentPlanId } = fulfillmentPlan ?? {};
      const accountPackages = Global.selectSellerPackages(state) || [];
      const updatePayload = OrderDetailsHelperService.buildCswPersistPayload(fulfillmentPlan, accountPackages);
      return of(
        CSWActions.setupShipmentConfiguration(fulfillmentPlanId, updatePayload, {
          type: getIsServiceLocalDelivery(state, updatePayload?.serviceId) ? LOCAL_DELIVERY : STANDARD,
        }),
      );
    }),
  );
};

export const printShipmentLabelEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_PREVIEW),
    mergeMap((action) => {
      const state = getState();
      const { shipmentDetailModalId } = action;
      const fulfillmentPlanId = Global.selectShipmentDetailsFulfillmentPlanIdForShipmentDetailModalId(state, { shipmentDetailModalId });
      const fulfillmentIds =
        Global.selectEntitiesFulfillmentPlanProperty(state, {
          fulfillmentPlanId,
          property: 'fulfillmentIds',
        }) || [];
      const [fulfillmentId] = fulfillmentIds;
      if (fulfillmentId) {
        const url = `${DocumentType.Label.apiDownloadPath}/${fulfillmentId}`;
        return of(PrintActions.openPrintModal(url, DocumentType.Label.identifier, [fulfillmentPlanId], [fulfillmentId]));
      }
      return EMPTY;
    }),
  );
};

export const shipmentNotifyMarketplaces = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_NOTIFY_MARKETPLACE_REQUEST),
    mergeMap((action) => {
      const state = getState();
      const { shipmentDetailModalId } = action;
      const fulfillmentPlanId = Global.selectShipmentDetailsFulfillmentPlanIdForShipmentDetailModalId(state, { shipmentDetailModalId });
      const requestPayload = Global.selectEntitiesFulfillmentPlanProperty(state, { fulfillmentPlanId, property: 'fulfillmentIds' });
      return ShipmentDetailsApi.notifyMarketplace(requestPayload).pipe(
        map((response) => {
          const fulfillmentsUpdated = response.data;
          return Actions.shipmentNotifyMarketplaceSuccess(fulfillmentsUpdated, shipmentDetailModalId);
        }),
        catchError((error) => {
          return of(Actions.shipmentNotifyMarketplaceFailure(error, requestPayload.shipmentIds));
        }),
      );
    }),
  );
};

export const clearShipFromCustomerDetailsTracker = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_DETAILS_MODAL_CLOSED),
    mergeMap(() => {
      const state = getState();
      const shipmentFromCustomerDetails = Global.selectShipmentDetailsShipmentFromCustomerDetails(state);
      if (shipmentFromCustomerDetails) {
        return of(Actions.clearShipmentFromCustomerDetailsTracker());
      }
      return EMPTY;
    }),
  );
};

export const closeCustomerDetailShipmentDetailsModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.GET_SHIPMENT_DETAILS_SUCCESS),
    mergeMap(() => {
      const modalId = get(Global.selectShipmentDetailsShipmentDetails(getState()), 'modalId', '');
      ModalService.closeModalById(modalId);
      return EMPTY;
    }),
  );
};

export const validateShipmentDetailsShipToAddress = (action$) => {
  return action$.pipe(
    ofType(Actions.VALIDATE_ADDRESS_SHIPMENT_DETAILS_SHIPTO),
    mergeMap((action) => {
      const { shipmentId, address } = action;
      return AddressApi.validateAddress(address).pipe(
        mergeMap((response) => {
          const verifiedAddress = response[0];
          const { status } = verifiedAddress;

          if (status === ERROR || status === WARNING) {
            return of(Actions.validateShipmentDetailsShipToAddressFailure(verifiedAddress));
          }

          return of(Actions.validateShipmentDetailsShipToAddressSuccess(shipmentId));
        }),
        catchError((error) => {
          return of(Actions.validateShipmentDetailsShipToAddressFailure(error));
        }),
      );
    }),
  );
};

export const updateShipmentDetailsShipToAddress = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.VALIDATE_ADDRESS_SHIPMENT_DETAILS_SHIPTO_SUCCESS),
    mergeMap((action) => {
      const { shipmentId } = action;
      const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(getState(), { fulfillmentPlanId: shipmentId });
      const shipTo = get(fulfillmentPlan, 'shipTo');

      shipTo.verificationStatus = VER;
      shipTo.verificationUtc = moment.utc().format();

      return updateFulfillmentPlanConfig(shipmentId, {
        ...fulfillmentPlan,
        shipTo,
      }).pipe(
        mergeMap(() => EMPTY),
        catchError(() => EMPTY),
      );
    }),
  );
};
